import { useEffect, useMemo } from 'react';

import { PreviewHeader } from '@/features/workflows/components/simple/preview-screens';
import { cn } from '@/lib/utils';

import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { formatResultDateTime } from '../../sessionDateTime';
import { extractError } from '../pan/pan.utils';

const STATUS_STYLE: Record<string, string> = {
  Passed: 'bg-[#DCFCE7] text-[#166534]',
  Review: 'bg-[#FEF9C3] text-[#854D0E]',
  Failed: 'bg-[#FEE2E2] text-[#991B1B]',
  Pending: 'bg-[#F3F4F6] text-[#374151]',
};

// Read-only Bank Account step detail for the view / review screens. Shows the
// stored, normalised verification outcome — no re-run form. Reports not-ready so
// the review panel's verify button stays disabled for this inspection view.
export function BankDetail({
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  useEffect(() => {
    onPendingChange(false);
    onReadyChange(false);
  }, [onPendingChange, onReadyChange]);

  const error = extractError(step.resultJson);
  const claims = useMemo(() => extractClaims(step.resultJson), [step.resultJson]);

  return (
    <form id={formId} onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-4">
      <PreviewHeader title="Bank Account Verification" subtitle="Account & name-match result" />

      <div className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
        <span className="text-sm text-[#6B7280]">Status</span>
        <span
          className={cn(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold',
            STATUS_STYLE[step.status] ?? STATUS_STYLE.Pending,
          )}
        >
          {step.status}
        </span>
      </div>

      {claims.length > 0 && (
        <div className="flex flex-col divide-y divide-[#F3F4F6] rounded-lg border border-[#E5E7EB]">
          {claims.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm text-[#6B7280]">{label}</span>
              <span className="text-sm font-medium text-[#111827]">{value}</span>
            </div>
          ))}
        </div>
      )}

      {error && step.status === 'Failed' && <p className="text-xs text-[#BF3636]">{error}</p>}
    </form>
  );
}

// Canonical Bank Return-Data fields (spec §8.5), in display order. The stored
// result is the NORMALISED, filtered object (BankResultNormalizer +
// ApplyReturnFieldFilter), so we render exactly the keys the admin left enabled.
const BANK_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: 'account_number', label: 'Account Number' },
  { key: 'account_holder_name', label: 'Account Holder Name' },
  { key: 'bank_name', label: 'Bank Name' },
  { key: 'ifsc', label: 'IFSC' },
  { key: 'account_status', label: 'Account Status' },
  { key: 'bank_reference', label: 'Bank Reference' },
  { key: 'name_match_score', label: 'Name Match Score' },
  { key: 'name_matched_against', label: 'Name Matched Against' },
  { key: 'verification_status', label: 'Status' },
  { key: 'verification_attempts', label: 'Verification Attempts' },
  { key: 'verified_at', label: 'Verified At' },
  { key: 'method_used', label: 'Method Used' },
];

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key === 'name_match_score') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : `${n.toFixed(0)}%`;
  }
  if (key === 'verified_at') {
    return formatResultDateTime(value);
  }
  return String(value);
}

function extractClaims(resultJson: string | null): [string, string][] {
  if (!resultJson) return [];
  let parsed: Record<string, unknown> | null = null;
  try {
    const p = JSON.parse(resultJson);
    parsed = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
  } catch {
    return [];
  }
  if (!parsed) return [];
  const out: [string, string][] = [];
  for (const { key, label } of BANK_FIELD_LABELS) {
    if (key in parsed && parsed[key] !== null && parsed[key] !== '') {
      out.push([label, formatValue(key, parsed[key])]);
    }
  }
  return out;
}
